import { PushProcessingNotification } from "@repo/helpers";
import { BasicDialog, Button, ButtonSize, DateTimePicker, TextArea } from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { useLocationApi } from "../../api/locationApi";
import * as Queries from "../../api/queries";
import { useSettingsApi } from "../../api/settingsApi";
import { useLocationContext } from "../../contexts/editableItemContext";
import { ActionButton } from "../../models/settings";
import style from "./style.module.scss";

interface Props {
	button: ActionButton;
}
export function StatusActionButtons() {
	const context = useLocationContext();
	const { useActionButtonsQuery } = useSettingsApi();
	const { data: actionButtons, loaded } = useActionButtonsQuery(context.locationId);
	if (!loaded) return null;
	return (
		<div className={style.actionButtons}>
			{actionButtons.map((button) => (
				<StatusActionButton key={button.statusId} button={button} />
			))}
		</div>
	);
}
export function StatusActionButton(props: Props) {
	const { button } = props;
	const context = useLocationContext();
	const [startDate, setStartDate] = useState<Date | undefined>(new Date());
	const [endDate, setEndDate] = useState<Date | undefined>(undefined);
	const [notes, setNotes] = useState<string>("");
	const [showDetailsModal, setShowDetailsModal] = useState(false);
	const locationApi = useLocationApi();
	const queryClient = useQueryClient();

	const save = useCallback(async () => {
		const notify = PushProcessingNotification("Saving Location...");
		try {
			await locationApi.updateLocationStatus(context.locationId, {
				statusId: button.statusId,
				action: button.name,
				startDate,
				endDate,
				notes,
			});
			await queryClient.invalidateQueries({ queryKey: [Queries.GROUP.LOCATION] });

			notify.success("Location status updated");
		} catch (ex) {
			notify.error("Failed to update location status");
			throw ex;
		} finally {
			setShowDetailsModal(false);
		}
	}, [locationApi, context.locationId, button, startDate, endDate, notes, queryClient]);

	return (
		<>
			<Button size={ButtonSize.large} color={button.color as any} onClick={() => setShowDetailsModal(true)}>
				{button.text}
			</Button>

			<BasicDialog open={showDetailsModal} title={button.text} fullWidth={false} hideTrigger allowOverflow={true}>
				<div className={style.statusUpdateContent}>
					<TextArea value={notes} changeHandler={setNotes} label="Notes" rows={4} />
					<DateTimePicker
						dateOnly
						value={startDate}
						changeHandler={(val) => setStartDate(val as Date)}
						label="Effective Start Date"
					/>
					{button.allowEndDate && (
						<DateTimePicker
							dateOnly
							value={endDate}
							changeHandler={(val) => setEndDate(val as Date)}
							label="Expected End Date"
						/>
					)}
				</div>
				<div className={style.statusUpdateActions}>
					<Button size={ButtonSize.large} color={"green"} onClick={save}>
						Save
					</Button>
					<Button size={ButtonSize.large} color={"red"} onClick={() => setShowDetailsModal(false)}>
						Cancel
					</Button>
				</div>
			</BasicDialog>
		</>
	);
}
